import {
	Component,
	EventListener,
	startComponents,
	stopComponents,
	triggerPublicEvent,
	getComponentByDomNode,
} from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { IGScrollToTargetHandlerOptions } from '@swisslife/shared-types/src/types/gondel-public-events';
import { dispatchCustomEvent, EventNames } from '@swisslife/shared-ui/src/utils/custom-event/js/custom-event';
import { Overlay, Modifier as OverlayModifier } from '../../overlay/js/overlay';
import { ReCaptcha } from './initReCaptcha';
import { FileUpload } from '../../../molecules/file-upload/js/file-upload';
import 'formdata-polyfill';

import ajax from '@swisslife/shared-ui/src/utils/ajax/js/ajax';

import { IValidationApi, IErrorTemplate } from './initParsley';

enum States {
	LoaderVisible = 'state-a-loader--visible',
	Error = 'state-o-form--error',
}

enum Selectors {
	Overlay = '.js-o-page__overlay',
	ReCaptcha = '.js-m-form-element__re-captcha',
	ContentWrapper = '.js-o-form__content-wrapper',
	SuccessOutlet = '.js-o-form__success-outlet',
	Loader = '.js-o-form__loader',
	FormElement = '.js-m-form-element',
	FileUpload = '.js-m-file-upload',
	CheckboxGroup = '.js-m-checkbox-group--track',
	RadiobuttonGroup = '.js-m-radiobutton-group--track',
}

enum ServerSideValidationStates {
	Failed = 'validation_error',
	Passed = 'validation_ok',
}

interface IValidationFailedEntries {
	[field: string]: string[];
}

interface IValidationInfo {
	failedValidationEntries: IValidationFailedEntries;
	successValidationEntries: string[];
}

interface IValidationResult {
	result: ServerSideValidationStates;
	validationInfo: IValidationInfo;
}

interface IFieldServerSideErrorCallback {
	[fieldName: string]: ServerSideErrorCallbackFunction;
}

type ValidationInitCallbackFunction = () => any | never;
type ServerSideErrorCallbackFunction = (
	errorMessageArray: string[],
	errorTemplate: IErrorTemplate,
	isFirstError: boolean
) => any | never;

export type ValidateCallbackFunction = () => any | never;

@Component('Form')
export class Form extends GondelJqueryComponent<HTMLFormElement> {
	private reCaptcha: ReCaptcha;
	private submitAjax: boolean;
	private target: string;
	private validationUri: string;
	private submitUri: string;
	private isReInvokedNativeSubmit: boolean = false;
	private isInOverlay: boolean = false;
	private isFirstFormInteraction: boolean = true;
	private overlay: Overlay;
	private validationInstance: IValidationApi;
	private formValidationInitCallbacks: ValidationInitCallbackFunction[] = [];
	private fieldServerSideErrorCallbacks: IFieldServerSideErrorCallback = {};
	private $contentWrapper: JQuery<HTMLElement>;
	private $successOutlet: JQuery<HTMLElement>;
	private $loader: JQuery<HTMLElement>;

	public start() {
		this.submitAjax = this.$ctx.data('submit-ajax') || false;
		this.target = this.$ctx.attr('target') || '';
		this.validationUri = this.$ctx.data('validation-uri') || '';
		this.submitUri = this.$ctx.attr('action') || '';
		this.$contentWrapper = $(Selectors.ContentWrapper, this.$ctx);
		this.$successOutlet = $(Selectors.SuccessOutlet, this.$ctx);
		this.$loader = $(Selectors.Loader, this.$ctx);

		const $overlay = this.$ctx.closest(Selectors.Overlay);

		if (this.$ctx.closest(Selectors.Overlay).length > 0) {
			this.overlay = getComponentByDomNode<Overlay>($overlay.get(0));
			this.isInOverlay = true;
			this.overlay.addModifier(OverlayModifier.Form);
		}
	}

	public async sync() {
		$(Selectors.ReCaptcha, this.$ctx)
			.toArray()
			.forEach(async (reCaptcha) => await this.initReCaptcha($(reCaptcha)));

		this.initForm();
	}

	public addGroupValidation(selector: string, stateClass: string, callbackFn: ValidateCallbackFunction) {
		this.validationInstance.addGroupValidation(selector, stateClass, callbackFn);
	}

	public registerValidationInitCallback(fnCallback) {
		this.formValidationInitCallbacks.push(fnCallback);
	}

	public registerFieldServerSideErrorCallback(fieldName: string, callbackFn: ServerSideErrorCallbackFunction) {
		this.fieldServerSideErrorCallbacks[fieldName] = callbackFn;
	}

	public updateValidationAfterContentChange() {
		this.validationInstance.triggerRefresh();
	}

	public handleFormValidation = ($field: JQuery<HTMLElement>) => {
		if ($field.length > 0) {
			dispatchCustomEvent(this.$ctx, EventNames.FormValidationFailed);
			this.scrollToTarget($field.closest(Selectors.FormElement));
			$field.focus();
		}
	};

	@EventListener('gFormInteractionEvent')
	public _handleFormInteractionEvent() {
		if (this.isFirstFormInteraction) {
			dispatchCustomEvent(this.$ctx, EventNames.FormInteraction);
			this.isFirstFormInteraction = false;
		}
	}

	@EventListener('submit')
	public _handleFormSubmit(ev: Event) {
		this.toggleLoadingState(true);

		// do not interfere native submit for case submit via reload and already passed validation
		if (this.isReInvokedNativeSubmit) {
			return;
		}

		ev.preventDefault();
		this.submitForm();
	}

	private isFormInitialized = async (): Promise<boolean> => {
		if (this.reCaptcha !== undefined && !this.reCaptcha.isLoaded) {
			return false;
		}

		if (this.reCaptcha !== undefined && !(await this.reCaptcha.execute())) {
			return false;
		}

		if (this.validationUri === '') {
			return false;
		}
		return true;
	};

	private isFormValid = async (): Promise<boolean> => {
		const serializedData = this.getFormSerializedData();

		try {
			const validationResult = await ajax<IValidationResult>(this.validationUri, {
				method: 'POST',
				dataType: 'json',
				data: serializedData,
			});

			// Stop if any server validation failed
			if (validationResult.result === ServerSideValidationStates.Failed) {
				this.showServerSideValidationErrors(validationResult);

				return false;
			}
			return true;
		} catch (error) {
			this.$ctx.addClass(States.Error);
			const statusCode = error.status ? error.status.toString() : 'n/a';
			dispatchCustomEvent(this.$ctx, EventNames.NetworkError, { statusCode });
			return false;
		}
	};

	private submitForm = async (): Promise<void> => {
		if (!(await this.isFormInitialized())) {
			this.toggleLoadingState(false);
			return;
		}
		if (!(await this.isFormValid())) {
			this.toggleLoadingState(false);
			dispatchCustomEvent(this.$ctx, EventNames.FormValidationFailed);
			return;
		}

		const eventData = {};
		const selectedFormData = this.collectEnhancedAnalyticsData();
		if (selectedFormData) {
			$.extend(eventData, { selectedFormOptions: selectedFormData });
		}

		if (this.submitAjax) {
			const extendType = { submitType: 'ajax' };
			$.extend(eventData, extendType);
			dispatchCustomEvent(this.$ctx, EventNames.FormSubmit, eventData);
			this.doAjaxSubmit();
		} else {
			const extendType = { submitType: 'reload' };
			$.extend(eventData, extendType);
			// the else case happens for submit via page reload or custom action which submit via target _blank
			dispatchCustomEvent(this.$ctx, EventNames.FormSubmit, eventData);

			// trigger form submit again and let the normal submit via page load happen
			this.isReInvokedNativeSubmit = true;
			this.$ctx.submit();

			// reset values again to be able to submit custom action form multiple times
			if (this.target === '_blank') {
				this.isReInvokedNativeSubmit = false;
				this.toggleLoadingState(false);
			}
		}
	};

	private async initForm() {
		// tslint:disable-next-line
		const asyncModule = await import(/* webpackChunkName: "dynamic-imports/parsley" */ './initParsley');

		this.validationInstance = asyncModule.initialize(this.$ctx);
		this.validationInstance.addValidatedCallback(this.handleFormValidation);

		for (const callback of this.formValidationInitCallbacks) {
			callback();
		}
	}

	private showServerSideValidationErrors(validationResult: IValidationResult) {
		const validationErrors = validationResult.validationInfo.failedValidationEntries;
		let isFirstError = true;

		for (const property in validationErrors) {
			if (validationErrors.hasOwnProperty(property)) {
				const fieldName = property;
				const errorMessageArray = validationErrors[property];
				const $inputField = this.$ctx.find(`[name="${fieldName}"]`);

				if ($inputField.length > 0) {
					this.validationInstance.addErrors($inputField, errorMessageArray);
				}

				if ($inputField.length > 0 && isFirstError) {
					this.scrollToTarget($inputField);
				}

				// add possible field server side error callback
				if (this.fieldServerSideErrorCallbacks[fieldName] !== undefined) {
					this.fieldServerSideErrorCallbacks[fieldName](
						errorMessageArray,
						this.validationInstance.getErrorTemplate(),
						isFirstError
					);
				}

				if (isFirstError) {
					isFirstError = false;
				}
			}
		}
	}

	private async doAjaxSubmit() {
		if (this.submitUri === '') {
			return;
		}

		const formData = new FormData(this.$ctx.get(0) as HTMLFormElement);

		try {
			const submitResult = await ajax<string>(this.submitUri, {
				method: 'POST',
				type: 'text/json',
				dataType: 'html',
				contentType: false, // needed to work in combination with file-upload
				processData: false, // needed to work in combination with file-upload
				data: formData,
			});

			stopComponents(this.$contentWrapper);

			this.$contentWrapper.remove();
			this.$successOutlet.append(submitResult);

			if (this.overlay) {
				this.overlay.removeModifier(OverlayModifier.Form);
			}

			startComponents(this.$successOutlet);

			this.toggleLoadingState(false);
			this.scrollToTarget(this.$successOutlet);
		} catch (error) {
			this.$ctx.addClass(States.Error);
			this.toggleLoadingState(false);
			const statusCode = error.status ? error.status.toString() : 'n/a';
			dispatchCustomEvent(this.$ctx, EventNames.NetworkError, { statusCode });
		}
	}

	private initReCaptcha = async ($element: JQuery) => {
		this.reCaptcha = new ReCaptcha({
			$element,
			sitekey: $element.data('sitekey'),
			onVerified: this.submitForm,
		});
		await this.reCaptcha.fetch();
	};

	private scrollToTarget($target: JQuery<HTMLElement>) {
		if (this.isInOverlay) {
			this.overlay.scrollToTarget($target);
		} else {
			const eventOptions: IGScrollToTargetHandlerOptions = {
				target: $target,
				duration: 500,
			};

			triggerPublicEvent('gScrollToTargetHandler', this, undefined, eventOptions);
		}
	}

	private toggleLoadingState(newState: boolean): void {
		if (this.$loader.length === 0) {
			return;
		}

		if (newState) {
			this.$loader.addClass(States.LoaderVisible);
		} else {
			this.$loader.removeClass(States.LoaderVisible);
		}
	}

	private getFormSerializedData(): string {
		let serializedFormData = this.$ctx.serialize();

		const $fileUploadFields = this.$ctx.find(Selectors.FileUpload);

		$.each($fileUploadFields, (index, fileUploadField) => {
			const fileUpload = getComponentByDomNode<FileUpload>(fileUploadField);
			const validationString = fileUpload.getFileValidationData();
			if (validationString !== '') {
				if (serializedFormData) {
					serializedFormData += `&${encodeURI(validationString)}`;
				} else {
					serializedFormData += encodeURI(validationString);
				}
			}
		});

		return serializedFormData;
	}

	private collectEnhancedAnalyticsData(): string {
		const $chk = this.$ctx.find(Selectors.CheckboxGroup);
		const $rb = this.$ctx.find(Selectors.RadiobuttonGroup);
		const $enhancedElements = $chk.add($rb);
		const serializedData = $enhancedElements.serializeArray();
		const analyticsData: string[] = [];

		serializedData.forEach((val) => {
			analyticsData.push(`${val.name} - ${val.value}`);
		});
		return analyticsData.join(',');
	}
}
